/* eslint-disable max-lines */
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import { lighten } from '@mui/material/styles';
import { capitalize, differenceBy, includes, isEmpty, map, omit, values, xorBy } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Form } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';

import AutoCompleteMultipleSelect from 'common/forms/AutoCompleteMultipleSelect';
import CheckboxField from 'common/forms/CheckboxField';
import { validateMaxNumber, validateQuantity } from 'common/forms/formValidations';
import TextField from 'common/forms/TextField';
import PatientBanner from 'common/modules/PatientBanner';
import { transformAndGenerateHash } from 'common/utils';
import ShipByDateCell from 'common/components/ShipByDateCell';
import { ReshippingReasons } from 'enums/requestStatus.enum';
import { createReshipment } from 'store/thunks/ordersListThunk';

import NewPatientAddressButton from '../ShippingTab/NewPatientAddressButton';

const LabelValueComp = ({ value, label }) => {
  return (
    <Grid container direction='column'>
      <Grid item>
        <Typography
          sx={{ color: (theme) => lighten(theme.palette.text.primary, 0.3), fontSize: '0.85em' }}
        >
          {label}
        </Typography>
      </Grid>
      <Grid item justifyContent='flex-start' alignItems='center'>
        <Typography sx={{ fontWeight: '500', minHeight: 20 }} component='span'>
          {value}
        </Typography>
      </Grid>
    </Grid>
  );
};

LabelValueComp.propTypes = {
  value: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
};

const ReshipOrderButton = ({ mpi, lineItems, orderId, partnerUuid, onSuccess }) => {
  const dispatch = useDispatch();
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [patientAddresses, setPatientAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState('');
  const [currentOrderShippingAddress, setCurrentOrderShippingAddress] = useState(null);
  const [initialPatientAddresses, setInitialPatientAddresses] = useState([]);
  const [hasInitializedShippingAddress, setHasInitializedShippingAddress] = useState(false);
  const [unVerifiedAddress, setUnVerifiedAddress] = useState([]);
  const [isManuallyMarkedSelected, setIsManuallyMarkedSelected] = useState(false);

  const filteredLineItems = lineItems.filter(
    (lineItem) =>
      lineItem.lineItemStatus === 'PRODUCT_FULFILLED' &&
      includes(['SHIPPED', 'RESHIPMENT'], lineItem.processingState)
  );

  const partners = useSelector(({ partner, auth }) => partner[auth?.currentTenantId]);
  const isPartnerSuspended = !!partners?.find(
    (partnerRecord) => partnerRecord.partnerUuid === partnerUuid
  )?.isSuspended;

  const alreadyInReshipment = useSelector(({ patient }) => {
    const activeOrders = patient[mpi]?.orders ?? [];
    return new Set(
      activeOrders
        .filter((o) => o.reshipmentOf)
        .flatMap((o) =>
          (o.lineItems ?? [])
            .filter((item) => item.processingState !== 'CANCELED' && item.rxNumber)
            .map((item) => item.rxNumber)
        )
    );
  });

  const patientAddress = useSelector(({ patient }) => patient[mpi]?.addresses);

  const patientOrderHistory = useSelector(({ patient }) => patient[mpi]?.orderHistory);

  const currentOrder = patientOrderHistory?.find((order) => order?.orderId === orderId);

  const initialOrderShipping = currentOrder?.shippingAddress;

  useEffect(() => {
    // patientAddress is undefined until getPatient populates the store. Mapping it would yield
    // undefined and overwrite the initial [] state, making the render-time find() below throw.
    // An empty array is a valid "loaded, no addresses" state and must still fall through.
    if (!patientAddress) return;

    const unVerifiedAddressIds = patientAddress
      ?.filter((item) => !item.isVerified)
      .map((item) => item.patientAddressId);

    if (unVerifiedAddressIds) {
      setUnVerifiedAddress(unVerifiedAddressIds);
    }

    const formatted = patientAddress?.map((item) => {
      const { addressLine1, addressLine2, city, state, zip } = item.address;
      const fullAddress = [addressLine1, addressLine2, `${city}, ${state} ${zip}`]
        .filter(Boolean)
        .join(', ');

      return { ...item, fullAddress };
    });

    setPatientAddresses(formatted);

    if (!hasInitializedShippingAddress) {
      setHasInitializedShippingAddress(true);

      setInitialPatientAddresses(formatted);
      setCurrentOrderShippingAddress(initialOrderShipping);

      const matchedAddress = patientAddress?.find((add) => {
        const { address } = add;

        const patientAddressString = `${address.addressLine1}${address.addressLine2 || ''}${
          address.city
        }${address.state}${address.zip}`;

        const shippingAddressString = `${initialOrderShipping.line1}${
          initialOrderShipping.line2 || ''
        }${initialOrderShipping.city}${initialOrderShipping.state}${initialOrderShipping.zip}`;

        const patientAddressHash = transformAndGenerateHash(patientAddressString);

        const orderShippingAddressHash = transformAndGenerateHash(shippingAddressString);

        return orderShippingAddressHash === patientAddressHash;
      });

      if (matchedAddress) {
        if (matchedAddress.isVerified) {
          setSelectedAddressId(matchedAddress.patientAddressId);
        }
      }

      return;
    }

    const newAddedAddress = differenceBy(formatted, initialPatientAddresses, 'patientAddressId');

    if (!isEmpty(newAddedAddress)) {
      const newAddress = newAddedAddress[0];

      if (newAddress.isVerified) {
        setSelectedAddressId(newAddress.patientAddressId);
      }

      const { addressLine1, addressLine2, city, state, zip } = newAddress.address;
      setCurrentOrderShippingAddress({
        line1: addressLine1,
        line2: addressLine2,
        city,
        state,
        zip,
      });

      setIsManuallyMarkedSelected(false);
    }
  }, [
    hasInitializedShippingAddress,
    initialOrderShipping,
    initialPatientAddresses,
    patientAddress,
  ]);

  const selectedAddress = patientAddresses.find(
    (address) => address.patientAddressId === selectedAddressId
  );

  // Clear a stale selection if the chosen address is no longer in the list (e.g. it was deleted or
  // the address list reloaded). Prevents submitting a dangling patientAddressId with a null
  // addressDirectoryId, which previously made the backend reship to the original order's address.
  useEffect(() => {
    if (selectedAddressId && !selectedAddress) {
      setSelectedAddressId('');
      setCurrentOrderShippingAddress(null);
    }
  }, [selectedAddress, selectedAddressId]);

  const handleExistingAddressCheckboxChange = (value) => {
    const isChecked = value.target.checked;
    setIsManuallyMarkedSelected(isChecked);
  };

  const handleCloseModal = (_, reason) => {
    /* istanbul ignore next */
    if (reason !== 'backdropClick') {
      setSelectedItems([]);
      setShowModal(false);
      setIsLoading(false);
    }
  };

  const handleOpenModal = () => {
    setSelectedItems([]);
    setShowModal(true);
  };

  const handleCheckboxChange = (lineItem) => {
    setSelectedItems((prev) => xorBy(prev, [lineItem], 'lineItemId'));
  };

  const handleAddressSelectChange = (event, form) => {
    const selectedPatientAddressId = event.target.value;
    setSelectedAddressId(selectedPatientAddressId);

    setIsManuallyMarkedSelected(false);
    form.change('addressValidation', false);

    const selectedOrdersAddress = patientAddresses.find(
      (address) => address.patientAddressId === selectedPatientAddressId
    );

    if (selectedOrdersAddress) {
      const { addressLine1, addressLine2, city, state, zip } = selectedOrdersAddress.address;

      const formattedShippingAddress = {
        line1: addressLine1,
        line2: addressLine2,
        city,
        state,
        zip,
      };

      setCurrentOrderShippingAddress(formattedShippingAddress);
    }
  };

  const handleReshipOrder = (formData) => {
    setIsLoading(true);
    const reshippingInput = [];

    const { deliveryInstructions } = formData;
    const formDataWithoutAddressValidation = omit(formData, [
      'addressValidation',
      'deliveryInstructions',
    ]);

    Object.keys(formDataWithoutAddressValidation).forEach((key) => {
      const reasons = formDataWithoutAddressValidation[key].reasonsForReshipping.map((reason) => {
        return Object.keys(ReshippingReasons).find(
          (keyVal) => ReshippingReasons[keyVal] === reason
        );
      });

      reshippingInput.push({
        lineItemId: JSON.parse(key),
        reasonForReshipment: reasons,
        quantityToReship: Number(formData[key].quantityToReship),
      });
    });

    const selectedLineItemIds = selectedItems.map((item) => item.lineItemId);
    const filteredReShippingInput = reshippingInput.filter((item) =>
      includes(selectedLineItemIds, item.lineItemId)
    );

    dispatch(
      createReshipment({
        input: filteredReShippingInput,
        mpi,
        patientAddressId:
          selectedAddressId &&
          (selectedAddressId ||
            (unVerifiedAddress.includes(selectedAddressId) && isManuallyMarkedSelected))
            ? selectedAddressId
            : undefined,
        addressDirectoryId: selectedAddress?.addressDirectoryId,
        ...(deliveryInstructions && { deliveryInstructions }),
        ...(isManuallyMarkedSelected &&
          selectedAddress?.isVerified === false && {
            isManuallyVerified: true,
            isValidationRequired: false,
          }),
      })
    )
      .then((isCreated) => {
        if (isCreated) {
          enqueueSnackbar(`Reshipment for line item(s) has been created successfully`, {
            variant: 'success',
          });
          onSuccess?.();
        }
      })
      .finally(() => {
        setIsLoading(false);
        handleCloseModal();
      });
  };

  return (
    <>
      <Dialog
        open={showModal}
        onClose={handleCloseModal}
        aria-describedby='ReshipOrder-Dialog-header'
        fullWidth
        maxWidth={false}
        PaperProps={{
          sx: { maxWidth: '700px' },
        }}
      >
        <Form
          onSubmit={handleReshipOrder}
          initialValues={{ deliveryInstructions: currentOrder?.deliveryInstructions || '' }}
          render={({ handleSubmit, invalid, form }) => (
            <form noValidate onSubmit={handleSubmit}>
              <DialogContent sx={{ minWidth: 500 }}>
                <Grid container sx={{ mb: 2 }}>
                  <Typography variant='h6'>{`Reship Order ${orderId}`}</Typography>
                </Grid>

                <Grid container sx={{ mb: 2 }}>
                  <PatientBanner mpi={mpi} />
                </Grid>

                <Grid container sx={{ mb: 2 }}>
                  <Typography>Select Item(s) to reship</Typography>
                </Grid>

                <Grid>
                  {map(filteredLineItems, (lineItem) => (
                    <Grid
                      container
                      key={lineItem.lineItemId}
                      alignItems='center'
                      sx={{ mb: 2, border: '1px solid #ccc', borderRadius: '4px', p: 0.8 }}
                    >
                      <Grid container>
                        <Grid item xs={1}>
                          <Tooltip
                            title={
                              alreadyInReshipment.has(lineItem.rxNumber)
                                ? 'An active reshipment already exists for this item'
                                : ''
                            }
                          >
                            <span>
                              <Checkbox
                                id={lineItem.lineItemId}
                                size='small'
                                disabled={alreadyInReshipment.has(lineItem.rxNumber)}
                                checked={selectedItems.some(
                                  (item) => item.lineItemId === lineItem.lineItemId
                                )}
                                onChange={() => handleCheckboxChange(lineItem)}
                                inputProps={{
                                  'aria-label': `mark ${lineItem.lineItemId} for reshipping`,
                                }}
                              />
                            </span>
                          </Tooltip>
                        </Grid>

                        <Grid item container justifyContent='space-between' xs={10} sx={{ mr: 1 }}>
                          <Grid item xs={6}>
                            <LabelValueComp label='Item' value={capitalize(lineItem.itemName)} />
                          </Grid>

                          <Grid item xs={2}>
                            <LabelValueComp label='Quantity' value={lineItem.quantity} />
                          </Grid>

                          <Grid item xs={3}>
                            <LabelValueComp label='Rx Number' value={lineItem.rxNumber} />
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  ))}
                </Grid>

                <Grid sx={{ mt: 4 }}>
                  {map(selectedItems, (item) => (
                    <Grid
                      key={item.lineItemId}
                      alignItems='center'
                      sx={{ border: '1px solid #ccc', borderRadius: '4px', p: 2, mb: 3 }}
                    >
                      <Grid container>
                        <Grid item xs={6}>
                          <Typography sx={{ mb: 1 }} variant='h6' fontWeight='600'>
                            {capitalize(item.itemName)}
                          </Typography>
                          <Typography sx={{ mb: 1 }}>
                            Strength: <strong>{item.strength}</strong>
                          </Typography>
                          <Typography sx={{ mb: 1 }}>
                            Last fill quantity: <strong>{item.quantity}</strong>
                          </Typography>
                          <Typography sx={{ mb: 1 }}>
                            Rx number: <strong>{item.rxNumber}</strong>
                          </Typography>
                          <Typography sx={{ mb: 1 }}>
                            Ship by date:{' '}
                            <ShipByDateCell shipByDate={item.shipByDate} tags={item.tags} />
                          </Typography>
                          <Tooltip title='Legacy field — see Ship By Date' placement='top' arrow>
                            <Typography sx={{ mb: 1 }} color='text.secondary'>
                              Needs by date: <strong>{item.needsByDate}</strong>
                            </Typography>
                          </Tooltip>
                          <Typography>
                            Drug form: <strong>{item.form}</strong>
                          </Typography>
                        </Grid>

                        <Grid item xs={6}>
                          <AutoCompleteMultipleSelect
                            name={`${JSON.stringify(item.lineItemId)}.reasonsForReshipping`}
                            label='Reason(s) for Reshipping'
                            id='reshipOrder-reasons'
                            options={values(ReshippingReasons)}
                            sortOptions
                            required
                          />

                          <TextField
                            id='reshipping-quantity'
                            label='Quantity to Reship'
                            name={`${JSON.stringify(item.lineItemId)}.quantityToReship`}
                            required
                            validations={[
                              validateMaxNumber(
                                'Quantity to reship cannot exceed original quantity',
                                item.quantity
                              ),
                              validateQuantity('Quantity to reship is required'),
                            ]}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  ))}
                </Grid>

                <Grid container justifyContent='space-between' alignItems='center' sx={{ mb: 2 }}>
                  <Grid item>
                    <Typography variant='h6' mb={1}>
                      Shipping Address
                    </Typography>
                  </Grid>
                  <Grid item>
                    <NewPatientAddressButton mpi={mpi} showDeliveryInstructions={false}>
                      Add New Address
                    </NewPatientAddressButton>
                  </Grid>
                </Grid>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    {currentOrderShippingAddress && (
                      <Box mt={-2} ml={1}>
                        <Typography>{currentOrderShippingAddress.line1}</Typography>
                        {currentOrderShippingAddress.line2 && (
                          <Typography>{currentOrderShippingAddress.line2}</Typography>
                        )}
                        <Typography>{currentOrderShippingAddress.city}</Typography>
                        <Typography>{currentOrderShippingAddress.state}</Typography>
                        <Typography>{currentOrderShippingAddress.zip}</Typography>

                        {selectedAddressId && unVerifiedAddress.includes(selectedAddressId) && (
                          <Box display='flex' alignItems='center' mt={1}>
                            <WarningAmberIcon sx={{ color: 'red', mr: 0.5, fontSize: 14 }} />
                            <Typography color='red' sx={{ fontSize: 12 }}>
                              Could not verify address
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    )}
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel id='select-existing-address-label'>
                        Select existing address
                      </InputLabel>
                      <Select
                        labelId='select-existing-address-label'
                        id='select-existing-address'
                        value={selectedAddressId}
                        label='Select another address'
                        onChange={(event) => handleAddressSelectChange(event, form)}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: 150,
                              overflowY: 'auto',
                            },
                          },
                        }}
                      >
                        {patientAddresses.map((address) => (
                          <MenuItem key={address.patientAddressId} value={address.patientAddressId}>
                            <Typography sx={{ whiteSpace: 'pre-line' }}>
                              {address.fullAddress}
                            </Typography>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />
                <Grid container sx={{ mt: 1 }}>
                  <Typography variant='h6' mb={1}>
                    Delivery Instructions
                  </Typography>
                  <TextField
                    id='ReshipOrder-deliveryInstructions-input'
                    label='Delivery Instructions'
                    name='deliveryInstructions'
                    maxLength={200}
                    multiline
                    rows={2}
                    helperText='Optional instructions for the carrier. For internal use only.'
                  />
                </Grid>

                {selectedAddressId && unVerifiedAddress.includes(selectedAddressId) && (
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                  </Grid>
                )}

                {selectedAddressId && unVerifiedAddress.includes(selectedAddressId) && (
                  <>
                    <Typography sx={{ mt: 2, ml: 1 }}>
                      We were unable to verify the entered address. Please review the options
                      carefully and confirm its accuracy before proceeding.
                    </Typography>

                    <Typography sx={{ mt: 2, fontWeight: 500, ml: 1 }}>
                      Mark address as verified and continue
                    </Typography>

                    <FormControlLabel
                      sx={{ ml: 1, mb: 0 }}
                      control={
                        <CheckboxField
                          id='AddressValidation-checkbox'
                          name='addressValidation'
                          checked={isManuallyMarkedSelected}
                          onChange={(value) => handleExistingAddressCheckboxChange(value)}
                          label={
                            currentOrderShippingAddress
                              ? `${currentOrderShippingAddress.line1}, ${
                                  currentOrderShippingAddress.line2 || ''
                                } ${currentOrderShippingAddress.city}, ${
                                  currentOrderShippingAddress.state
                                }, ${currentOrderShippingAddress.zip}`
                              : 'Loading address...'
                          }
                        />
                      }
                    />
                  </>
                )}
              </DialogContent>

              <DialogActions>
                <Button variant='outlined' color='secondary' onClick={handleCloseModal}>
                  Cancel
                </Button>
                <LoadingButton
                  loading={isLoading}
                  variant='contained'
                  type='submit'
                  disabled={
                    invalid ||
                    isEmpty(selectedItems) ||
                    isEmpty(selectedAddressId) ||
                    !selectedAddress?.addressDirectoryId ||
                    (unVerifiedAddress?.includes(selectedAddressId) && !isManuallyMarkedSelected)
                  }
                >
                  Submit
                </LoadingButton>
              </DialogActions>
            </form>
          )}
        />
      </Dialog>

      <Tooltip title={isPartnerSuspended ? 'This partner is suspended.' : ''} placement='top' arrow>
        <span>
          <LoadingButton
            loading={false}
            variant='contained'
            type='submit'
            size='small'
            disabled={isPartnerSuspended}
            sx={{
              px: 1,
              fontSize: '0.75rem',
              minHeight: '30px',
              lineHeight: 'normal',
            }}
            onClick={handleOpenModal}
          >
            Reship Order
          </LoadingButton>
        </span>
      </Tooltip>
    </>
  );
};

ReshipOrderButton.propTypes = {
  orderId: PropTypes.string.isRequired,
  mpi: PropTypes.string.isRequired,
  lineItems: PropTypes.arrayOf(
    PropTypes.shape({
      itemName: PropTypes.string,
      needsByDate: PropTypes.string,
      itemType: PropTypes.string,
      lineItemStatus: PropTypes.string,
      cost: PropTypes.number,
      lineItemId: PropTypes.string,
      prescriptionId: PropTypes.string,
    })
  ).isRequired,
  partnerUuid: PropTypes.string,
  onSuccess: PropTypes.func,
};

ReshipOrderButton.defaultProps = {
  partnerUuid: undefined,
  onSuccess: undefined,
};

export default ReshipOrderButton;
